// Firmware update over the computer's own Bluetooth (Web Bluetooth), replacing
// the nRF Connect hand-off. Two BLE connections, because the running app and
// the bootloader are different BLE devices:
//
//   1. The app — after the serial `dfu` command it advertises connectably as
//      "SIT-XXXX" (last 4 hex digits of its MAC). We write to its buttonless
//      DFU characteristic: first "advertise the bootloader as SIT_DFU_XXXX",
//      then "reboot into the bootloader".
//   2. The bootloader — found by that unique name (falling back to the stock
//      SIT_BL_BLE / DfuTarg names if a bootloader ignores the rename), then the
//      package is streamed with the Secure DFU protocol (nordicDfu.js).
//
// Chromium only lets requestDevice() run inside a user gesture, and step 2
// happens seconds after the operator's click. In Electron the main process
// re-enters the page with a gesture (executeJavaScript(…, true)) and picks the
// device itself — see "Bluetooth" in electron/main.js — so no chooser and no
// second click.

import {
  DFU_SERVICE,
  DFU_CONTROL_POINT,
  DFU_PACKET,
  DFU_BUTTONLESS,
  DfuError,
  enterBootloader,
  runSecureDfu,
} from "./nordicDfu";
import { parseWearableAdvert } from "./wearableAdvert";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const STOCK_BOOTLOADER_NAMES = ["SIT_BL_BLE", "DfuTarg"];

// Nordic LED Button Service. Writing 1 to its LED characteristic makes a
// wearable on USB blink white for 5 s ("identify", firmware v0.11.0+). The
// wearable then drops the connection itself.
const LBS_SERVICE = "00001523-1212-efde-1523-785feabcd123";
const LBS_LED = "00001525-1212-efde-1523-785feabcd123";

// Step log, shown as "[host] [dfu] …" lines in the serial output so a failed
// update can be diagnosed from the operator's paste.
export const dfuLog = (message) => {
  window.electronAPI?.ble?.log?.(message);
  // eslint-disable-next-line no-console
  console.info("[dfu]", message);
};

export const isBleDfuSupported = () =>
  typeof navigator !== "undefined" &&
  !!navigator.bluetooth &&
  !!window.electronAPI?.ble;

/** False when the adapter is missing, switched off, or access is denied. */
export const isBluetoothAvailable = async () => {
  try {
    return !!(await navigator.bluetooth?.getAvailability?.());
  } catch (e) {
    return false;
  }
};

/** Subscribe to adapter on/off changes. Returns an unsubscribe function. */
export const onBluetoothAvailabilityChanged = (callback) => {
  const listener = (e) => callback(!!e.value);
  navigator.bluetooth?.addEventListener?.("availabilitychanged", listener);
  return () =>
    navigator.bluetooth?.removeEventListener?.("availabilitychanged", listener);
};

/** BLE names for a wearable, from the MAC its status reports (any format). */
export const bleNamesForMac = (mac) => {
  const suffix = String(mac || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase()
    .slice(-4);
  if (suffix.length !== 4) return null;
  return { app: `SIT-${suffix}`, bootloader: `SIT_DFU_${suffix}` };
};

// Advertising names a scan looks for: the app in DFU advertising ("SIT-XXXX"),
// our renamed bootloader ("SIT_DFU_XXXX") and the stock bootloader names.
const SCAN_PREFIXES = ["SIT-", "SIT_DFU_", ...STOCK_BOOTLOADER_NAMES];

/**
 * What a scanned advertising name means for an update, or null if it isn't a
 * wearable we can update.
 *   app        "SIT-XXXX": running firmware, in DFU advertising (buttonless)
 *   bootloader "SIT_DFU_XXXX": already in our bootloader
 *   stock      "SIT_BL_BLE" / "DfuTarg": a bootloader every unit shares the
 *              name of, so it must be addressed by its scanned device id
 */
export const bleTargetFromName = (name) => {
  const n = String(name || "").trim();
  if (STOCK_BOOTLOADER_NAMES.includes(n)) {
    return { kind: "stock", code: null, names: { app: null, bootloader: n } };
  }
  const m = /^SIT(-|_DFU_)([0-9A-F]{4})$/i.exec(n);
  if (!m) return null;
  const code = m[2].toUpperCase();
  return {
    kind: m[1] === "-" ? "app" : "bootloader",
    code,
    names: bleNamesForMac(code),
  };
};

/**
 * Scan for wearables advertising for an update. `onDevices` gets the growing
 * list ([{ id, name, target }]) as devices appear; resolves with the final
 * list. stopBleScan() ends it early.
 */
export const scanForDfuDevices = async ({
  durationMs = 10000,
  onDevices,
} = {}) => {
  const withTargets = (list) =>
    (list || [])
      .map((d) => ({ ...d, target: bleTargetFromName(d.name) }))
      .filter((d) => d.target);
  window.__sitBleScan = () =>
    navigator.bluetooth
      .requestDevice({
        filters: SCAN_PREFIXES.map((namePrefix) => ({ namePrefix })),
        optionalServices: [DFU_SERVICE],
      })
      // Always cancelled by the main process once the scan ends.
      .then(
        () => true,
        () => false
      );
  const off = window.electronAPI.ble.onScanDevices((list) =>
    onDevices?.(withTargets(list))
  );
  try {
    dfuLog(`scanning for ${SCAN_PREFIXES.join(" | ")}`);
    const { devices } = await window.electronAPI.ble.scan({ durationMs });
    const found = withTargets(devices);
    dfuLog(`scan found: ${found.map((d) => d.name).join(", ") || "nothing"}`);
    return found;
  } finally {
    off();
    delete window.__sitBleScan;
  }
};

export const stopBleScan = () => window.electronAPI?.ble?.stopScan?.();

/**
 * Watch the adverts of nearby SIT-XXXX wearables: `onAdverts` gets a Map of
 * name → parsed advert plus `seenAt` (ms, its latest advert), or null when it
 * carries no payload, on every change.
 * Returns a function that stops watching. No-op outside the desktop app.
 */
export const watchWearableAdverts = (onAdverts) => {
  const watch = window.electronAPI?.ble?.watchAdverts;
  if (!watch) return () => {};
  return watch((list) =>
    onAdverts(
      new Map(
        (list || []).map((d) => {
          const advert = parseWearableAdvert(d.mfr);
          return [d.name, advert && { ...advert, seenAt: d.seenAt }];
        })
      )
    )
  );
};

// The main process picks the first name in `names` it sees, or — after a short
// grace period — a lone device carrying one of the later names. With
// `deviceId` it waits for that exact scanned device instead.
const requestDevice = async (
  names,
  deviceId = null,
  services = [DFU_SERVICE]
) => {
  let device = null;
  let error = null;
  window.__sitBleRequestDevice = () =>
    navigator.bluetooth
      .requestDevice({
        filters: [...new Set(names)].map((name) => ({ name })),
        optionalServices: services,
      })
      .then(
        (d) => {
          device = d;
          return true;
        },
        (e) => {
          error = e;
          return false;
        }
      );
  try {
    dfuLog(`scanning for ${names.join(" | ")}`);
    const { reason } = await window.electronAPI.ble.requestDevice({
      names,
      deviceId,
    });
    if (device) {
      dfuLog(`found ${device.name}`);
      return device;
    }
    dfuLog(`scan ended: ${reason || error?.message || "no device"}`);
    if (reason === "ambiguous" || reason === "not-found") {
      throw new DfuError(reason, `${reason}: ${names.join(", ")}`, { names });
    }
    throw new DfuError("bluetooth", error?.message || "Bluetooth unavailable");
  } finally {
    delete window.__sitBleRequestDevice;
  }
};

const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(message)), ms)
    ),
  ]);

// A freshly started advertiser sometimes refuses the first connection. macOS
// never times a connection attempt out by itself, so we do. `setup(service)`
// (finding characteristics, subscribing) runs inside the retry too: macOS
// sometimes hands over a stale copy of a device's services, seen as "GATT
// Service no longer exists" or a missing characteristic, and only a fresh
// connection reads them again. Resolves with what setup returns.
const connect = async (
  device,
  service = DFU_SERVICE,
  setup = (s) => s,
  attempts = 3
) => {
  for (let i = 1; ; i++) {
    try {
      const server = await withTimeout(
        device.gatt.connect(),
        10000,
        "connection timed out"
      );
      const found = await withTimeout(
        server.getPrimaryService(service),
        10000,
        "service discovery timed out"
      );
      return await setup(found);
    } catch (e) {
      dfuLog(`connect attempt ${i} to ${device.name} failed: ${e.message}`);
      if (device.gatt.connected) device.gatt.disconnect();
      if (i >= attempts) {
        if (e instanceof DfuError) throw e;
        throw new DfuError(
          "connect",
          `Couldn't connect to ${device.name}: ${e.message}`
        );
      }
      await sleep(800);
    }
  }
};

const waitForDisconnect = (device, timeoutMs) =>
  new Promise((resolve) => {
    if (!device.gatt.connected) return resolve();
    const done = () => {
      clearTimeout(timer);
      device.removeEventListener("gattserverdisconnected", done);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    device.addEventListener("gattserverdisconnected", done);
  });

const subscribe = async (characteristic, handler) => {
  const listener = (e) => {
    const v = e.target.value;
    handler(
      new Uint8Array(v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength))
    );
  };
  characteristic.addEventListener("characteristicvaluechanged", listener);
  await characteristic.startNotifications();
  return () =>
    characteristic.removeEventListener("characteristicvaluechanged", listener);
};

/**
 * Update a wearable over Bluetooth. The device must already be advertising as
 * SIT-XXXX (serial `dfu`, or docked on firmware v0.9.0+) unless
 * `fromBootloader` is set: the retry after a failed attempt, when the unit is
 * either still waiting in its bootloader or, after a dropped transfer, back on
 * its old firmware. Then whichever it advertises as is taken: the bootloader
 * at once, the app after a short grace.
 *
 * @param {object} opts
 * @param {string} [opts.mac]
 * @param {{app: ?string, bootloader: string}} [opts.names]
 *   BLE names to use instead of the ones derived from `mac` (a scanned target)
 * @param {string} [opts.deviceId]
 *   scanned device to connect to first (needed for stock bootloader names)
 * @param {{initPacket: Uint8Array, firmware: Uint8Array}} opts.pkg
 * @param {boolean} [opts.fromBootloader]
 * @param {(stage: string) => void} [opts.onStage]
 *   connectingApp | rebooting | connectingBootloader | transferring | finishing
 * @param {(p: {sent: number, total: number}) => void} [opts.onProgress]
 */
export const updateFirmwareOverBle = async ({
  mac,
  names: namesOverride,
  deviceId = null,
  pkg,
  fromBootloader = false,
  onStage,
  onProgress,
}) => {
  const names = namesOverride || bleNamesForMac(mac);
  if (!names) throw new DfuError("not-found", "Device MAC unknown");
  const t0 = Date.now();
  const at = () => `+${((Date.now() - t0) / 1000).toFixed(1)} s`;

  let first = null;
  if (fromBootloader) {
    onStage?.("connectingBootloader");
    first = await requestDevice(
      [names.bootloader, names.app, ...STOCK_BOOTLOADER_NAMES].filter(Boolean),
      deviceId
    );
    dfuLog(`${at()} found ${first.name}`);
  }
  if (!fromBootloader || first.name === names.app) {
    onStage?.("connectingApp");
    const app = first || (await requestDevice([names.app], deviceId));
    dfuLog(`${at()} found ${app.name}`);
    await rebootIntoBootloader(app, names, onStage);
    dfuLog(`${at()} ${app.name} is rebooting`);
    first = null;
    // Give the bootloader a moment to come up and start advertising.
    await sleep(1000);
  }

  onStage?.("connectingBootloader");
  // The pinned device is only the first one we connect to: once the app
  // reboots, its bootloader is a different BLE device.
  const bootloader =
    first ||
    (await requestDevice([names.bootloader, ...STOCK_BOOTLOADER_NAMES]));
  dfuLog(`${at()} found ${bootloader.name}`);
  const notifyListeners = [];
  const { controlPoint, packet } = await connect(
    bootloader,
    DFU_SERVICE,
    async (service) => {
      const cp = await service.getCharacteristic(DFU_CONTROL_POINT);
      const pk = await service.getCharacteristic(DFU_PACKET);
      await subscribe(cp, (bytes) =>
        notifyListeners.forEach((fn) => fn(bytes))
      );
      return { controlPoint: cp, packet: pk };
    }
  );
  try {
    const disconnectListeners = [];
    const onGattDisconnect = () => disconnectListeners.forEach((fn) => fn());
    bootloader.addEventListener("gattserverdisconnected", onGattDisconnect);

    onStage?.("transferring");
    dfuLog(
      `${at()} connected to bootloader ${bootloader.name}; sending ${
        pkg.initPacket.length
      } B init + ${pkg.firmware.length} B firmware`
    );
    const sendStart = Date.now();
    try {
      await runSecureDfu({
        transport: {
          writeControl: (bytes) => controlPoint.writeValueWithResponse(bytes),
          writePacket: (bytes) => packet.writeValueWithoutResponse(bytes),
          isConnected: () => bootloader.gatt.connected,
          onControlNotify: (fn) => {
            notifyListeners.push(fn);
            return () => notifyListeners.splice(notifyListeners.indexOf(fn), 1);
          },
          onDisconnect: (fn) => {
            disconnectListeners.push(fn);
            return () =>
              disconnectListeners.splice(disconnectListeners.indexOf(fn), 1);
          },
        },
        initPacket: pkg.initPacket,
        firmware: pkg.firmware,
        onProgress,
        log: (m) => dfuLog(`${at()} ${m}`),
      });
    } finally {
      bootloader.removeEventListener(
        "gattserverdisconnected",
        onGattDisconnect
      );
    }
    const sendS = (Date.now() - sendStart) / 1000;
    // The bootloader activates the image and resets on its own.
    onStage?.("finishing");
    dfuLog(
      `${at()} transfer complete in ${sendS.toFixed(1)} s (${Math.round(
        pkg.firmware.length / 1024 / sendS
      )} KB/s); bootloader is activating the new image`
    );
    await waitForDisconnect(bootloader, 10000);
  } finally {
    if (bootloader.gatt.connected) bootloader.gatt.disconnect();
  }
};

// Connect to the running app and ask it, through buttonless DFU, to reboot
// into its bootloader advertising as `names.bootloader`.
const rebootIntoBootloader = async (app, names, onStage) => {
  const listeners = [];
  const buttonless = await connect(app, DFU_SERVICE, async (service) => {
    let ch;
    try {
      ch = await service.getCharacteristic(DFU_BUTTONLESS);
    } catch (e) {
      throw new DfuError("buttonless", "Device has no buttonless DFU service");
    }
    await subscribe(ch, (bytes) => listeners.forEach((fn) => fn(bytes)));
    return ch;
  });
  try {
    onStage?.("rebooting");
    dfuLog(
      `connected to ${app.name}; asking it to reboot into the bootloader as ${names.bootloader}`
    );
    await enterBootloader(
      {
        write: (bytes) => buttonless.writeValueWithResponse(bytes),
        onIndicate: (fn) => {
          listeners.push(fn);
          return () => listeners.splice(listeners.indexOf(fn), 1);
        },
      },
      { advName: names.bootloader }
    );
    dfuLog("device accepted; waiting for it to drop the connection");
    await waitForDisconnect(app, 10000);
    dfuLog(
      app.gatt.connected
        ? "still connected after 10 s"
        : "app disconnected (rebooting)"
    );
  } finally {
    if (app.gatt.connected) app.gatt.disconnect();
  }
};

/**
 * Make a docked wearable (on USB, running firmware) blink white for 5 s so it
 * can be found in a full case. Connects, writes 1 to the LED characteristic
 * and lets the wearable drop the link. Tries the desktop's noble scanner first
 * (no new scan, so about a second), then Web Bluetooth. Firmware before v0.11.0 accepts the
 * write but doesn't blink.
 *
 * @param {object} opts
 * @param {string} opts.name  advertising name ("SIT-XXXX")
 * @param {string} [opts.deviceId]  scanned device to connect to
 */
export const identifyOverBle = async ({ name, deviceId = null }) => {
  // Fast path: the desktop's own scanner (noble) connects to a unit it has
  // just heard, skipping the chooser scan below.
  const fast = await window.electronAPI?.ble?.identify?.(name);
  if (fast?.ok) {
    dfuLog(`identify ${name}: sent`);
    return;
  }
  if (fast) dfuLog(`identify ${name}: fast path ${fast.reason}; scanning`);
  const device = await requestDevice([name], deviceId, [LBS_SERVICE]);
  try {
    const service = await connect(device, LBS_SERVICE);
    const led = await service.getCharacteristic(LBS_LED);
    dfuLog(`connected to ${device.name}; asking it to identify`);
    await led.writeValueWithResponse(new Uint8Array([1]));
    await waitForDisconnect(device, 3000);
  } finally {
    if (device.gatt.connected) device.gatt.disconnect();
  }
};

/**
 * Erase every record on a docked wearable (firmware v0.14.0+): the desktop's
 * noble scanner connects, writes the sync clear command and waits for the
 * unit to report. Resolves when cleared; rejects with a DfuError whose code
 * is not-found | refused | erase-failed | timeout | unavailable | failed.
 */
export const clearOverBle = async ({ name, onProgress }) => {
  const clear = window.electronAPI?.ble?.clear;
  if (!clear)
    throw new DfuError("unavailable", "Clearing needs the desktop app");
  dfuLog(`clear ${name}: asking`);
  const res = await clear(name, onProgress);
  if (!res?.ok) {
    dfuLog(`clear ${name}: ${res?.reason || "failed"}`);
    throw new DfuError(
      res?.reason || "failed",
      res?.error || res?.reason || "failed"
    );
  }
  dfuLog(`clear ${name}: cleared`);
};
